import React, { useEffect } from 'react';
import { Router, Route } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import ReactGA from 'react-ga4';
import { ConfigProvider, Modal } from 'antd';

import * as actions from './store/actions';

// Remove old antd CSS import (antd v5 uses CSS-in-JS)
// import './assets/css/antd.css';
import './assets/scss/global.scss?raw';
import './assets/scss/layout.scss?raw';
import styles from './App.scss';

import MainContainer from './containers/MainContainer/MainContainer';
import Spinner from './components/UI/Spinner/Spinner';
import ErrorHandler from './HOC/ErrorHandler';
import ModalManager from './components/ModalManager/ModalManager';
import ReconnectionModal from './components/ReconnectionModal/ReconnectionModal';
import config from './config';
import { parseTheme } from './Utils/serializer';

import { defaultTheme } from './const';

// Analytics hooks
import { useGlobalClickTracking } from './hooks/useGlobalClickTracking';
import { useGlobalFormTracking } from './hooks/useGlobalFormTracking';

ReactGA.initialize(config.ANALYTICS_ID);

const App = ({ history }) => {
  const dispatch = useDispatch();

  // Enable global analytics tracking via data-track attributes
  useGlobalClickTracking();
  useGlobalFormTracking();

  const { loading, modal, auth } = useSelector(state => ({
    loading: state.global.loading,
    modal: state.global.modal,
    auth: state.auth,
  }));
  const themeData = auth.company_details?.theme_data
    ? parseTheme(auth.company_details.theme_data)
    : defaultTheme;

  useEffect(() => {
    if (localStorage.getItem('access-token') && localStorage.getItem('refresh-token')) {
      dispatch(actions.getUserInfoRequest());
    }
  }, [dispatch]);

  useEffect(() => {
    if (window.location.host === 'assesment-qa.codehall.in') {
      ReactGA.send({ hitType: 'pageview', page: window.location.pathname });
    }
  }, [history.location.pathname]);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: themeData.secondaryColor,
        },
        components: {
          Button: {
            colorPrimary: themeData.secondaryColor,
          },
        },
      }}
    >
      <div className={styles.app}>
        <Router history={history}>
          <ErrorHandler>
            <MainContainer />
            <Modal
              title={modal.title}
              open={modal.visible}
            >
              {modal.content}
            </Modal>
            <ModalManager />
            <ReconnectionModal />
            {loading && <Spinner />}
          </ErrorHandler>
        </Router>
      </div>
    </ConfigProvider>
  );
};

App.propTypes = {
  history: PropTypes.object.isRequired,
};

export default App;
